import React from "react";
import { ApiContext } from "../common/context/ApiContext";
import { useLiveData } from "../common/utils";
import { Account, AccountContext } from "../common/context/AccountContext";
import { TabEnum, TabsContext } from "../common/context/TabsContext";
import { useLoadingIndicator } from "../common/context/LoadingIndicatorContext";
import { useTranslation } from "react-i18next";
import { Row } from "react-bootstrap";
import { BackNav, FlexRow, PageContainer } from "../common/CommonStyles";
import { Link } from "react-router-dom";
import { SchoolInsightReportInfo, SchoolList } from "../common/api";

export const SchoolListPage: React.FC = () => {
  const { t } = useTranslation();
  const { getSchoolList, getSchoolInsightReports } =
    React.useContext(ApiContext);
  const { accountLiveData } = React.useContext(AccountContext);
  const { setCurrentTab } = React.useContext(TabsContext);
  const [account, setAccount] = React.useState<Account>();
  const { startLoading, stopLoading } = useLoadingIndicator(true);
  const listFetchedForUserVersion = React.useRef<number | null>(null);
  const fetchJobForUserVersion = React.useRef<number | null>(null);
  const [schoolList, setSchoolList] = React.useState<SchoolList | null>(null);
  // Every openable report per school, keyed by school id. The endpoint returns newest-period-first
  // within a school, so each list keeps that order and the current year leads.
  const [reportsBySchool, setReportsBySchool] = React.useState<
    Map<number, SchoolInsightReportInfo[]>
  >(new Map());

  React.useEffect(() => {
    setCurrentTab(TabEnum.SchoolList);
  }, [setCurrentTab]);

  useLiveData(
    React.useCallback(async (account: Account) => {
      if (account.hasUserAndMaybeProfile()) {
        setAccount(account);
      }
    }, []),
    accountLiveData
  );

  React.useEffect(() => {
    (async () => {
      if (!account) {
        return;
      }
      if (
        account.userCurrentVersion !== listFetchedForUserVersion.current &&
        account.userCurrentVersion !== fetchJobForUserVersion.current
      ) {
        startLoading();
        const expectedVersion = (fetchJobForUserVersion.current =
          account.userCurrentVersion);
        // Fetched together: both are gated by the same permission, and a school row is more
        // useful with its report link than without it.
        const [schools, insightReports] = await Promise.all([
          getSchoolList(),
          getSchoolInsightReports(),
        ]);
        if (expectedVersion !== fetchJobForUserVersion.current) {
          return;
        }
        setSchoolList(schools);
        const bySchool = new Map<number, SchoolInsightReportInfo[]>();
        for (const report of insightReports.reports) {
          if (report.html_url) {
            const reports = bySchool.get(report.school_id) ?? [];
            reports.push(report);
            bySchool.set(report.school_id, reports);
          }
        }
        setReportsBySchool(bySchool);
        listFetchedForUserVersion.current = fetchJobForUserVersion.current;
        stopLoading();
      }
    })();
  }, [
    account,
    getSchoolList,
    getSchoolInsightReports,
    startLoading,
    stopLoading,
  ]);

  if (!schoolList) {
    return <></>;
  }

  return (
    <PageContainer>
      <Row className="align-items-center">
        <FlexRow>
          <BackNav label={t("generic_nav_back")} />
        </FlexRow>
      </Row>
      <div className="text-center mt-4 mb-3">
        <h3>{t("school_list_schools_title")}</h3>
      </div>
      <div>
        <table className="table align-middle">
          <thead>
            <tr>
              <th scope="col">{t("school_list_school")}</th>
              <th scope="col">{t("school_list_district")}</th>
              <th scope="col">{t("school_list_tutoring_value")}</th>
              <th scope="col">{t("school_list_l1")}</th>
              <th scope="col">{t("school_list_l7")}</th>
              <th scope="col">{t("school_list_l28")}</th>
              <th scope="col">{t("school_list_active")}</th>
              <th scope="col">{t("school_list_report")}</th>
              <th scope="col" className="text-end">
                {t("school_list_last_synced")}
              </th>
            </tr>
          </thead>
          <tbody>
            {schoolList.schools.map((info, index) => (
              <tr key={index}>
                <td>
                  <Link
                    className="text-decoration-none"
                    to={`/schools/dashboard/?school_id=${info.id}`}
                  >
                    {info.name}
                  </Link>
                </td>
                <td>{info.district_name}</td>
                <td>
                  $
                  {t("intcomma", {
                    val: info.tutoring_value_dollars,
                  })}
                </td>
                <td>
                  $
                  {t("intcomma", {
                    val: info.l1,
                  })}
                </td>
                <td>
                  $
                  {t("intcomma", {
                    val: info.l7,
                  })}
                </td>
                <td>
                  $
                  {t("intcomma", {
                    val: info.l28,
                  })}
                </td>
                <td>{info.is_active ? "✅" : "❌"}</td>
                <td>
                  {reportsBySchool.has(info.id) ? (
                    reportsBySchool.get(info.id)!.map((report) => (
                      // A plain link, not a router route: this is a stored HTML document served
                      // by the API, authenticated by the JWT cookie set at login.
                      <div key={report.id}>
                        <a
                          href={report.html_url!}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {report.period_label}
                        </a>
                      </div>
                    ))
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
                <td className="text-end">{info.last_synced}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PageContainer>
  );
};
